/**
 * FAQ data for industry landing pages. Used for both display and FAQPage schema.
 */

export interface FaqItem {
  question: string;
  answer: string;
}

export const neverMissACallFaqs: FaqItem[] = [
  {
    question: "How quickly can I set this up?",
    answer:
      "Setup takes just 5-10 minutes. After signing up, you'll get a phone number and customize your responses. Your AI receptionist will be answering calls immediately.",
  },
  {
    question: "How much does it cost?",
    answer:
      "Sale plans start at $29.97/month for 50 calls with SAVE40. Compare that to $25,000-40,000 annually for a full-time receptionist. We offer a 7-day free trial so you can test it risk-free.",
  },
  {
    question: "Can I use my existing phone number?",
    answer:
      "Yes! You can forward your existing business line to your AI receptionist, or get a new dedicated number through Greet. Both options work perfectly.",
  },
  {
    question: "What if the AI can't answer a question?",
    answer:
      "The AI will politely tell the caller you'll call them back, then sends you a detailed transcript with their contact info so you can follow up personally. You never lose a lead.",
  },
  {
    question: "Can it schedule appointments?",
    answer:
      "Yes! The AI can automatically book, reschedule, and manage appointments directly in your calendar. Customers can schedule anytime, even outside business hours.",
  },
];

export const plumbingFaqs: FaqItem[] = [
  {
    question:
      "Will customers know they're talking to AI? Will they trust an AI for emergency plumbing?",
    answer:
      "Our AI sounds natural and professional-customers often think it's a real person. For emergencies, they're just relieved someone answered immediately at 2 AM. The AI captures exactly what they need, and when you call back in 10 minutes with solutions, you build massive trust.",
  },
  {
    question: "Can the AI really diagnose plumbing problems?",
    answer:
      "The AI asks diagnostic questions to understand the issue-is it one drain or all? Is there a smell? Is water backing up? Based on answers, it can identify burst pipes, clogs, water heater issues, etc. You get a detailed summary before calling back, saving you hours on phone diagnostics.",
  },
  {
    question: "What if a customer calls about something unusual?",
    answer:
      "For complex or unusual issues, Greet seamlessly transfers the call to you with a full summary. You jump in knowing exactly what the customer said, so you never waste time repeating information.",
  },
  {
    question: "How does it handle emergency routing?",
    answer:
      "You set what qualifies as 'emergency'-burst pipes, sewage backup, gas leaks, etc. AI identifies these and either wakes you immediately or ensures they get your attention within minutes. Less urgent calls can be handled fully by AI or queued for your next available slot.",
  },
  {
    question: "Will this work with my 24/7 service model?",
    answer:
      "Perfect for 24/7 plumbers. The AI handles all after-hours calls, qualifies emergencies, and only wakes you for true urgent situations. You get real sleep while still catching every emergency.",
  },
  {
    question: "Can I still get my regular phone number?",
    answer:
      "Yes! Forward your current number to Greet or get a new local number. Either way, setup takes 10 minutes and your customers won't notice any change.",
  },
  {
    question: "What about seasonal work fluctuation?",
    answer:
      "Greet scales automatically. Winter has 5x call volume? No problem. The AI handles everything the same way. You go from 'drowning in calls' to 'we answered every single one.'",
  },
  {
    question: "How much does this actually cost vs. what I'll gain?",
    answer:
      "A full-time receptionist costs $30K-40K/year. An on-call answering service is $200-400/month. Greet's sale plans run $29.97-$119.97/month with SAVE40 and catch 100% of calls, not 70-80%. Even one emergency call per week at $500+ more than pays for itself.",
  },
];

export const salonFaqs: FaqItem[] = [
  {
    question: "Can the AI book complex color appointments correctly?",
    answer:
      "Absolutely. You set appointment types: 'Root Touch-up (45 min)', 'Full Color (2 hrs)', 'Balayage (3 hrs)', etc. AI asks the right questions: current color, desired look, previous processing. Books with the right duration and takes notes for your team.",
  },
  {
    question: "How does it handle specific stylist requests?",
    answer:
      "You set each stylist's specialties: 'Maria: Balayage expert', 'Sarah: Precision cuts', 'Amelia: Bridal makeup'. When a client requests a service or stylist, AI routes to the right person. Clients get better results, stylists get work they excel at.",
  },
  {
    question: "Will appointment reminders actually reduce no-shows?",
    answer:
      "Yes. Studies show 24-hour reminders reduce no-shows by 30-40%. Greet sends text/call reminders automatically. You'll notice the difference immediately-you go from 8-10 no-shows per week to 2-3.",
  },
  {
    question: "What about complex packages or combo services?",
    answer:
      "You can set packages like 'Mani-Pedi Combo' or 'Bridal Package' with bundled pricing. AI explains what's included, books the full duration, and notes the package. Clients love simplicity, your revenue per booking goes up.",
  },
  {
    question: "Can the AI handle cancellations and rescheduling?",
    answer:
      "Yes. Clients can cancel/reschedule via AI. You get notified immediately, the slot opens up for rebooking. Instead of a lost $150 slot, Greet immediately offers it to someone on the waiting list.",
  },
  {
    question: "What if a client has unusual requests or needs?",
    answer:
      "For anything complex, the AI smoothly transfers to you with full context. You jump in knowing their hair history, their desired look, any previous problems. Way more efficient than starting from scratch.",
  },
  {
    question: "How does this work with my online booking system?",
    answer:
      "Greet integrates with most salon software (Vagaro, Booksy, Mindbody, etc.). Appointments sync automatically. If you don't use software, Greet adds to your calendar and sends you confirmations.",
  },
  {
    question: "What's the ROI? Is this worth it for a small salon?",
    answer:
      "Even one extra full-color appointment per week ($200) pays for Greet. Most salons go from 40% chair utilization to 85%+. One stylist fully booked adds $30K-50K/year. The ROI is insane.",
  },
];

export const locksmithFaqs: FaqItem[] = [
  {
    question:
      "Will customers be comfortable talking to an AI about their emergency lockout?",
    answer:
      "Customers locked out at 3 AM are just relieved someone answered immediately. They want fast help, not a human-they want results. Our AI sounds professional and reassuring, asking the right questions to get them help fast. When you call back in minutes with a solution, they're grateful.",
  },
  {
    question:
      "Can the AI really assess whether it's a real emergency vs. a crank call?",
    answer:
      "Yes. The AI asks specific questions: Where are you locked out (home/car/office)? Do you have ID? How urgent is this? Based on answers, it flags genuine emergencies. You only get woken up for real situations.",
  },
  {
    question:
      "What if someone's aggressive or panicked about being locked out?",
    answer:
      "The AI is trained to handle stressed customers calmly and professionally. It de-escalates panic, gathers critical information (address, access methods), and ensures you have everything you need before you arrive.",
  },
  {
    question: "Can the AI handle commercial security consultations?",
    answer:
      "Absolutely. The AI can qualify commercial prospects, understand their security needs, and schedule consultations. You show up pre-prepped knowing exactly what they need-way more effective than cold calls.",
  },
  {
    question: "What if it's a complex job or unusual situation?",
    answer:
      "For anything complex, the AI transfers the call to you with a full summary of what the customer said. You never lose information and never waste time repeating yourself.",
  },
  {
    question: "How does this help with my 24/7 operation?",
    answer:
      "The AI handles all after-hours calls, qualifies emergencies, and only wakes you for genuine lockouts. You get real sleep while still catching every emergency and high-value call.",
  },
  {
    question:
      "Can I differentiate between home, car, and commercial lockouts?",
    answer:
      "Yes. The AI asks location-specific questions and tags calls appropriately. Emergency home lockouts get your immediate attention. Commercial jobs get scheduled for business hours.",
  },
  {
    question: "What's the ROI vs. just having an answering service?",
    answer:
      "Traditional answering services catch maybe 70% of calls and take poor notes. Greet catches 100% of calls, qualifies them, and ensures you only get interrupted for real opportunities. One extra lockout per week pays for itself.",
  },
];

export const restaurantsFaqs: FaqItem[] = [
  {
    question:
      "Will customers mind talking to an AI about their reservation?",
    answer:
      "Not at all. Restaurants already use automated booking systems. Our AI is more natural and conversational than typical phone systems. Customers just want a confirmed table-they don't care if it's AI or human as long as they're heard immediately.",
  },
  {
    question:
      "Can the AI handle special requests like 'I need a quiet table for a proposal'?",
    answer:
      "Yes! The AI collects special requests, dietary restrictions, occasion details, and seating preferences. All this gets to you so you can prepare and create exceptional experiences.",
  },
  {
    question:
      "What about customers asking complex questions about your menu or wine list?",
    answer:
      "You set up common questions (vegetarian options, allergens, happy hour times). For complex questions, the AI takes a message and you call back. The customer still feels heard immediately.",
  },
  {
    question: "Can the AI handle waitlists during busy times?",
    answer:
      "Absolutely. During peak hours, Greet automatically manages waitlists, offers the next available time, and updates customers when a table opens up-no manual work from you.",
  },
  {
    question: "Will this work for my catering and event business line?",
    answer:
      "Perfect for it. Greet can have a separate number or recognize catering inquiries and route them appropriately. Catering leads get pre-qualified with budget, date, and event details.",
  },
  {
    question: "How does this help with delivery and takeout calls?",
    answer:
      "You can customize Greet to handle dine-in reservations through one number and takeout/delivery through another. Each gets the right script and information.",
  },
  {
    question: "Can the AI work with my reservation system?",
    answer:
      "Yes. Greet integrates with Google Calendar and popular reservation platforms. Bookings sync automatically so you have one source of truth.",
  },
  {
    question:
      "What if we're fully booked? Can the AI suggest an alternative time or take a waitlist?",
    answer:
      "Exactly. The AI knows your availability, suggests the next open slot, or adds them to a waitlist with automatic notifications when something opens up. Smart business AND great customer service.",
  },
  {
    question: "Does Greet block spam and telemarketing calls?",
    answer:
      "Yes! Greet automatically protects you from spam calls using a database of 10.5M+ known spam numbers. Spam calls are still logged for your records, but you won't get notifications. You can also manually block any number. Focus on real customers, not telemarketers.",
  },
];

export const landscapersFaqs: FaqItem[] = [
  {
    question: "Will customers know they're talking to AI?",
    answer:
      "Modern AI voices sound remarkably natural. Most landscaping customers don't realize they're speaking with AI-they just think you have excellent customer service. Those who do know often appreciate the instant response and professionalism.",
  },
  {
    question: "What if someone has a complex question the AI can't answer?",
    answer:
      "Greet smoothly transfers calls to you with a complete summary of what the customer needs. You jump right into the conversation knowing their full situation, saving time and improving customer experience.",
  },
  {
    question: "How does it work with my Google Calendar?",
    answer:
      "Greet syncs directly with your Google Calendar. When the AI schedules an appointment, it automatically appears in your calendar with all customer details, location, and project scope. No manual entry needed.",
  },
  {
    question: "Will this work for emergency/storm damage calls?",
    answer:
      "Yes! Greet can be programmed to identify urgent situations like fallen trees or storm damage, flag them as emergencies, and ensure you get notified immediately even if you're on another job.",
  },
  {
    question: "What if I'm already too busy? Can I redirect all calls?",
    answer:
      "Absolutely. You can set it to handle all routine calls, collect information for non-emergency requests, and only interrupt you for true emergencies. Many landscapers run it 24/7 and only take emergency calls during off-hours.",
  },
  {
    question: "How long does setup take?",
    answer:
      "About 15 minutes. Add your business info, common questions/pricing, and connect your calendar. That's it. Your AI receptionist is live and ready to answer calls within the hour.",
  },
  {
    question: "What if I have multiple crews or team members?",
    answer:
      "Perfect for scaling. The AI can route calls to different team members based on location or specialty, book all appointments in one shared calendar, and provide detailed summaries to your entire team.",
  },
  {
    question: "Can I still use my existing phone number?",
    answer:
      "Yes! Forward your current business number to Greet, or get a new local number. Either way, it takes 5 minutes to set up and existing customers won't notice any change.",
  },
];

export const hardscapeContractorsFaqs: FaqItem[] = [
  {
    question: "How does Greet handle complex hardscape project inquiries?",
    answer:
      "Greet is trained to ask the right questions: project type (deck, patio, retaining wall), size, materials preference, timeline, and budget range. This pre-qualification means you only spend time on serious prospects with the budget for your work.",
  },
  {
    question: "Can Greet distinguish between DIY and contractor-needed projects?",
    answer:
      "Absolutely. Through intelligent questioning, Greet can identify whether the caller wants a simple project or needs professional expertise. You won't waste time on tire-kickers or DIY shoppers.",
  },
  {
    question: "What happens with emergency repairs (failed patios, cracking stonework)?",
    answer:
      "Greet can be configured to identify urgent/emergency situations and flag them for immediate response. These high-priority calls get your attention first-often before routine design consultations.",
  },
  {
    question: "Does this work for commercial hardscape projects?",
    answer:
      "Yes! Many hardscape contractors work on both residential and commercial projects. Greet can route calls appropriately and ensure commercial inquiries get treated as high-priority.",
  },
  {
    question: "Can Greet handle my seasonal scheduling?",
    answer:
      "Perfect for seasonal businesses. In off-season, Greet can handle inquiries and book consultations for when you're back to full operation. During peak season, it handles 80%+ of routine calls automatically.",
  },
  {
    question: "What if a customer has special material requests or complex designs?",
    answer:
      "For complex requests, Greet transfers the call to you with a complete summary of what the customer discussed. No information is lost, and you jump straight into the conversation with full context.",
  },
  {
    question: "How does this help with crew management and job assignment?",
    answer:
      "Greet can route calls to the right team member based on project type or location, ensuring your best person gets the highest-value leads. You can also set availability by crew size and capacity.",
  },
  {
    question:
      "Can Greet book multiple-site consultations (for clients with multiple properties)?",
    answer:
      "Yes! Greet can ask clarifying questions about all properties and book consultations accordingly. It collects all the details you need to prepare before arriving.",
  },
];
